import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter, Download, Upload, RefreshCw, ArrowUpRight, ArrowDownRight, FileText, MoreHorizontal, Loader2 } from 'lucide-react';
import axios from 'axios';
import { setupAxios } from '../../auth';
import { useAuth } from '../../auth';
import { ACCOUNTS_PERMS } from '../core/permissions';
import { AccountsActionDropdown, DropdownPosition, getDropdownPosition } from './AccountsActionDropdown';
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext';
const api = axios.create({ baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || '' });
setupAxios(api);

export default function AccountsSalesInvoices() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { state: globalState, vessels: globalVessels } = useGlobalFilter();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [invoices, setInvoices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null);

  const filters = ['All', 'Draft', 'Submitted', 'Overdue', 'Paid', 'Partially Paid'];

  const fetchInvoices = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (globalState.company) {
        params.append('companyId', globalState.company.id.toString());
      }
      if (globalState.vessel) {
        params.append('vesselId', globalState.vessel.id.toString());
      } else if (globalState.company && globalVessels.length > 0) {
        globalVessels.forEach((v: any) => params.append('vesselId', v.id.toString()));
      }
      const response = await api.get(`/accounts/invoices?${params.toString()}`);
      let data = response.data?.data || response.data || [];
      if (!Array.isArray(data)) data = [];
      
      // Client-side fallback filtering
      if (globalState.vessel) {
        data = data.filter((i: any) => !i.vesselId || String(i.vesselId) === String(globalState.vessel!.id));
      } else if (globalState.company && globalVessels.length > 0) {
        const vIds = new Set(globalVessels.map((v: any) => String(v.id)));
        data = data.filter((i: any) => !i.vesselId || vIds.has(String(i.vesselId)));
      }
      
      setInvoices(data);
    } catch (err: any) {
      console.error('Failed to fetch invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Stable key derived from vessel IDs — only changes when actual vessel list changes,
  // not on every context re-render (which would cause duplicate requests).
  const globalVesselIds = useMemo(
    () => globalVessels.map((v: any) => v.id).sort().join(','),
    [globalVessels]
  );

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState.company?.id, globalState.vessel?.id, globalVesselIds]);

  useEffect(() => {
    const closeDropdowns = () => {
      setActiveDropdown(null);
      setDropdownPos(null);
    };
    document.addEventListener('click', closeDropdowns);
    return () => document.removeEventListener('click', closeDropdowns);
  }, []);

  const safeNumber = (val: any): number => {
    let num = Number(val);
    if (isNaN(num)) {
      const clean = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : '';
      num = Number(clean);
      if (isNaN(num)) num = 0;
    }
    return num;
  };

  const getDerivedStatuses = (i: any): string[] => {
    const statuses: string[] = [];
    const baseStatus = (i.status || 'draft').toLowerCase();
    const status = baseStatus === 'sent' ? 'submitted' : baseStatus;

    if (['paid', 'cancelled', 'draft'].includes(status)) {
      statuses.push(status);
    } else {
      statuses.push(status);
    }

    if (i.dueDate && !['paid', 'cancelled', 'draft'].includes(status)) {
      const due = new Date(i.dueDate);
      const today = new Date();
      due.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      if (due < today) {
        if (!statuses.includes('overdue')) {
          statuses.push('overdue');
        }
      }
    }

    return statuses.length > 0 ? statuses : ['draft'];
  };

  // Compute stats from live data
  const totalInvoices = invoices.length;
  const paidValue = invoices.reduce((sum, i) => {
    const t = safeNumber(i.total || 0);
    const b = i.balanceDue !== undefined && i.balanceDue !== null ? safeNumber(i.balanceDue) : t;
    return sum + Math.max(0, t - b);
  }, 0);
  const outstandingInvoices = invoices.filter(i => getDerivedStatuses(i).some(s => ['submitted', 'partially paid', 'overdue'].includes(s)));
  const outstandingValue = outstandingInvoices.reduce((sum, i) => sum + safeNumber(i.balanceDue ?? i.total ?? 0), 0);
  const overdueInvoices = invoices.filter(i => getDerivedStatuses(i).includes('overdue'));
  const overdueValue = overdueInvoices.reduce((sum, i) => sum + safeNumber(i.balanceDue ?? i.total ?? 0), 0);

  const formatCurrency = (val: any) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safeNumber(val));
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const stats = [
    { label: 'Total Invoices', value: totalInvoices.toString(), change: '+0%', trend: 'up', color: 'blue' },
    { label: 'Paid', value: formatCurrency(paidValue), change: '+0%', trend: 'up', color: 'emerald' },
    { label: 'Outstanding', value: formatCurrency(outstandingValue), change: '0%', trend: 'up', color: 'amber' },
    { label: 'Overdue', value: formatCurrency(overdueValue), change: '0%', trend: 'down', color: 'red' },
  ];

  const filteredInvoices = invoices.filter(i => {
    if (activeFilter !== 'All') {
      const derived = getDerivedStatuses(i);
      const filterLower = activeFilter.toLowerCase();
      if (!derived.includes(filterLower)) return false;
    }
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    const matchInvoice = i.invoiceNumber ? i.invoiceNumber.toLowerCase().includes(searchLower) : false;
    const matchCustomer = i.customerName ? i.customerName.toLowerCase().includes(searchLower) : false;
    return matchInvoice || matchCustomer;
  });

  const handleDownloadCSV = () => {
    if (filteredInvoices.length === 0) {
      alert("No invoice data to export.");
      return;
    }
    const headers = ["Sr. No.", "Invoice #", "Customer", "Date", "Due Date", "Amount", "Balance", "Status"];
    const rows = filteredInvoices.map((invoice, index) => {
      const derived = getDerivedStatuses(invoice);
      const displayStatus = derived.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ');
      return [
        index + 1,
        invoice.invoiceNumber || '-',
        invoice.customerName || '-',
        formatDate(invoice.invoiceDate),
        formatDate(invoice.dueDate),
        formatCurrency(Number(invoice.total || 0)),
        formatCurrency(Number(invoice.balanceDue ?? invoice.total ?? 0)),
        displayStatus
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `invoices_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'paid') return 'bg-emerald-100 text-emerald-700';
    if (s === 'overdue') return 'bg-red-100 text-red-700';
    if (s === 'submitted') return 'bg-blue-100 text-blue-700';
    if (s === 'partially paid') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-700'; // Draft
  };

  const invoice = activeDropdown !== null
    ? filteredInvoices.find((invoice) => invoice.id === activeDropdown)
    : null;

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">

        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">

          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-5" id="main-scroll">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Accounts / Sales</div>
                <h1 className="text-xl font-bold text-slate-900 leading-tight">Invoices</h1>
                <p className="text-xs text-slate-500">Track and manage all sales invoices</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span>Data refreshed just now</span>
                  <button onClick={fetchInvoices} className="p-1 rounded hover:bg-slate-100 transition-colors" title="Refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin text-blue-500' : ''}`} />
                  </button>
                </div>
                {hasPermission(ACCOUNTS_PERMS.INVOICE_CREATE) && (
                  <button onClick={() => navigate('/accounts/sales/invoices/new')} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"><Plus className="w-4 h-4" />New Invoice</button>
                )}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-xl border border-red-100 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl bg-${stat.color}-50 flex items-center justify-center`}><FileText className={`w-4.5 h-4.5 text-${stat.color}-600`} /></div>
                    <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${stat.trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>{stat.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{stat.change}</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                  {filters.map(f => (<button key={f} onClick={() => setActiveFilter(f)} className={`px-3 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition-all ${activeFilter === f ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{f}</button>))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input type="text" placeholder="Search invoices..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-56 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all" /></div>
                  {/* Filter button kept for future use.
                  <button className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"><Filter className="w-4 h-4 text-slate-500" /></button>
                  */}
                  <button
                    onClick={handleDownloadCSV}
                    className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <Download className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto pb-24">
                <table className="w-full text-left min-w-175">
                  <thead>
                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50/50">
                      <th className="px-4 py-3 font-medium text-center text-slate-400">Sr. No.</th>
                      <th className="px-4 py-3 font-medium">Invoice #</th>
                      <th className="px-4 py-3 font-medium">Customer</th>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Due Date</th>
                      <th className="px-4 py-3 font-medium text-right">Amount</th>
                      <th className="px-4 py-3 font-medium text-right">Balance</th>
                      <th className="px-4 py-3 font-medium text-center">Status</th>
                      <th className="px-4 py-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {isLoading ? (
                      <tr>
                        <td colSpan={9} className="p-16 text-center">
                          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                          <p className="text-slate-500 font-medium">Loading invoices...</p>
                        </td>
                      </tr>
                    ) : filteredInvoices.length === 0 ? (
                      <tr>
                        <td colSpan={9}>
                          <div className="p-16 flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-linear-to-br from-blue-50 to-blue-100 text-blue-500 rounded-2xl flex items-center justify-center mb-5"><FileText className="w-10 h-10" strokeWidth={1.5} /></div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">{searchQuery ? 'No matching invoices' : 'No invoices found'}</h3>
                            <p className="text-slate-500 text-sm text-center max-w-sm mb-6">{searchQuery ? 'Try adjusting your search or filters to find what you are looking for.' : 'Create your first invoice and get paid faster with online payment links.'}</p>
                            {!searchQuery && (
                              <div className="flex items-center gap-3">
                                {hasPermission(ACCOUNTS_PERMS.INVOICE_CREATE) && (
                                  <button onClick={() => navigate('/accounts/sales/invoices/new')} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"><Plus className="w-4 h-4" />Create Invoice</button>
                                )}
                                <button className="px-5 py-2.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl text-sm font-bold transition-all flex items-center gap-2"><Upload className="w-4 h-4" />Import CSV</button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredInvoices.map((invoice, index) => (
                        <tr key={invoice.id} className="hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => hasPermission(ACCOUNTS_PERMS.INVOICE_UPDATE) && navigate(`/accounts/sales/invoices/${invoice.id}`)}>
                          <td className="px-4 py-3 text-slate-500 font-medium text-center" onClick={e => e.stopPropagation()}>
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 font-semibold text-blue-600 hover:underline">
                            {invoice.invoiceNumber || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-medium">
                            {invoice.customerName || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {formatDate(invoice.invoiceDate)}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {formatDate(invoice.dueDate)}
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-700 text-right">
                            {formatCurrency(Number(invoice.total || 0))}
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-700 text-right">
                            {formatCurrency(Number(invoice.balanceDue ?? invoice.total ?? 0))}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                              {getDerivedStatuses(invoice).map(s => (
                                <span key={s} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${getStatusBadge(s)}`}>
                                  {s.split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right relative">
                            <button
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all focus:opacity-100"
                              onClick={e => {
                                e.stopPropagation();
                                if (activeDropdown === invoice.id) {
                                  setActiveDropdown(null);
                                  setDropdownPos(null);
                                } else {
                                  setDropdownPos(getDropdownPosition(e.currentTarget as HTMLElement));
                                  setActiveDropdown(invoice.id);
                                }
                              }}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                            
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </main>
      </div>

      {invoice && (
        <AccountsActionDropdown open={true} position={dropdownPos}>
                                {hasPermission(ACCOUNTS_PERMS.INVOICE_UPDATE) && (
                                  <button
                                    className="w-full text-left px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                                    onClick={() => {
                                      setActiveDropdown(null);
                                      setDropdownPos(null);
                                      navigate(`/accounts/sales/invoices/${invoice.id}`);
                                    }}
                                  >
                                    Edit Invoice
                                  </button>
                                )}
                                {hasPermission(ACCOUNTS_PERMS.INVOICE_DELETE) && (
                                  <>
                                    <div className="my-1 border-t border-slate-100"></div>
                                    <button
                                      className="w-full text-left px-4 py-2 text-[13px] font-medium text-red-600 hover:bg-red-50 transition-colors"
                                      onClick={async () => {
                                        setActiveDropdown(null);
                                      setDropdownPos(null);
                                        if (window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
                                          try {
                                            await api.delete(`/accounts/invoices/${invoice.id}`);
                                            fetchInvoices();
                                          } catch (err: any) {
                                            console.error(err);
                                            alert('Failed to delete invoice');
                                          }
                                        }
                                      }}
                                    >
                                      Delete Invoice
                                    </button>
                                  </>
                                )}
        </AccountsActionDropdown>
      )}

    </>
  );
}
